import * as React from 'react';
import {
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Box,
    Collapse,
    alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import { useStore, useTranslate } from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoAppsOutline } from 'react-icons/io5';

import { APPS_REGISTRY, DEFAULT_ACTIVE_APP_ID, resolveAppIdFromPathname } from '../apps/appsRegistry';
import { ACTIVE_APP_STORE_KEY } from '../apps/activeAppStore';
import { useTenantApps } from '../apps/tenantApps';

/** Descriptions longer than this get an expand toggle instead of being clamped silently. */
const DESCRIPTION_CLAMP_CHARS = 32;

/**
 * Odoo-style 3×3 app grid launcher (opens app picker dialog).
 *
 * Tiles are compact icon-first cards; a long description collapses to one line
 * with a small chevron that expands it in place.
 */
export function OdooAppSwitcher() {
    const [open, setOpen] = React.useState(false);
    const [expandedId, setExpandedId] = React.useState<string | null>(null);
    const translate = useTranslate();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [storedAppId, setActiveAppId] = useStore<string>(ACTIVE_APP_STORE_KEY, DEFAULT_ACTIVE_APP_ID);
    const activeAppId = resolveAppIdFromPathname(pathname) ?? storedAppId;

    const { isLoaded, getInstalledApps, persistLastUsedApp } = useTenantApps();
    const launcherApps = isLoaded ? getInstalledApps() : APPS_REGISTRY;

    const closeDialog = () => {
        setOpen(false);
        setExpandedId(null);
    };

    const selectApp = (id: string, basePath: string, disabled?: boolean) => {
        if (disabled) return;
        setActiveAppId(id);
        persistLastUsedApp(id);
        sessionStorage.setItem('sessionActiveAppId', id);
        closeDialog();
        navigate(basePath, { replace: true });
    };

    return (
        <>
            <Tooltip title={translate('pos.odoo.apps', { _: 'Apps' })} enterDelay={400}>
                <IconButton
                    color="inherit"
                    onClick={() => setOpen(true)}
                    aria-label="Apps"
                    size="small"
                    sx={{ mr: 0.5 }}
                >
                    <IoAppsOutline size={22} style={{ display: 'block' }} />
                </IconButton>
            </Tooltip>
            <Dialog
                open={open}
                onClose={closeDialog}
                maxWidth="md"
                fullWidth
                slotProps={{ paper: { sx: { borderRadius: 3 } } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                    <AppsOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                        {translate('pos.odoo.all_apps', { _: 'All apps' })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {launcherApps.length}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'grid',
                            // minmax(0, …) — a plain 1fr floors at content width, which the
                            // single-line (nowrap) descriptions would push past the dialog.
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                sm: 'repeat(3, minmax(0, 1fr))',
                                md: 'repeat(4, minmax(0, 1fr))',
                            },
                            alignItems: 'start',
                            gap: 1.25,
                            pt: 0.5,
                            pb: 2,
                        }}
                    >
                        {launcherApps.map(app => {
                            const accent = app.accentColor ?? '#1976d2';
                            const AppIcon = app.icon;
                            const isActive = activeAppId === app.id;
                            const isExpanded = expandedId === app.id;
                            const hasMore =
                                !!app.description && app.description.length > DESCRIPTION_CLAMP_CHARS;

                            return (
                                <Box
                                    key={app.id}
                                    role="button"
                                    tabIndex={app.disabled ? -1 : 0}
                                    aria-current={isActive ? 'true' : undefined}
                                    onClick={() => selectApp(app.id, app.basePath, app.disabled)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            selectApp(app.id, app.basePath, app.disabled);
                                        }
                                    }}
                                    sx={{
                                        position: 'relative',
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        p: 1.25,
                                        borderRadius: 2,
                                        border: 1,
                                        borderColor: isActive ? alpha(accent, 0.6) : 'divider',
                                        bgcolor: isActive ? alpha(accent, 0.06) : 'background.paper',
                                        cursor: app.disabled ? 'default' : 'pointer',
                                        opacity: app.disabled ? 0.5 : 1,
                                        outline: 'none',
                                        transition: 'border-color .15s, box-shadow .15s, transform .15s',
                                        '&:hover': app.disabled
                                            ? undefined
                                            : {
                                                  borderColor: alpha(accent, 0.7),
                                                  boxShadow: `0 4px 14px ${alpha(accent, 0.18)}`,
                                                  transform: 'translateY(-2px)',
                                              },
                                        '&:focus-visible': {
                                            borderColor: accent,
                                            boxShadow: `0 0 0 3px ${alpha(accent, 0.25)}`,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                flexShrink: 0,
                                                width: 32,
                                                height: 32,
                                                borderRadius: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: alpha(accent, 0.12),
                                                color: accent,
                                            }}
                                        >
                                            {AppIcon ? (
                                                <AppIcon sx={{ fontSize: 20 }} />
                                            ) : (
                                                <AppsOutlinedIcon sx={{ fontSize: 20 }} />
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            noWrap
                                            sx={{ lineHeight: 1.2, minWidth: 0 }}
                                        >
                                            {app.name}
                                        </Typography>
                                    </Box>

                                    {app.description ? (
                                        <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Collapse in={isExpanded} collapsedSize={16}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: 'block',
                                                            lineHeight: '16px',
                                                            whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {app.description}
                                                    </Typography>
                                                </Collapse>
                                            </Box>
                                            {hasMore ? (
                                                <Tooltip
                                                    title={
                                                        isExpanded
                                                            ? translate('pos.odoo.less', { _: 'Less' })
                                                            : translate('pos.odoo.more', { _: 'More' })
                                                    }
                                                    enterDelay={500}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        aria-label={isExpanded ? 'Show less' : 'Show more'}
                                                        aria-expanded={isExpanded}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setExpandedId(isExpanded ? null : app.id);
                                                        }}
                                                        sx={{
                                                            p: 0.125,
                                                            color: 'text.secondary',
                                                            transform: isExpanded
                                                                ? 'rotate(180deg)'
                                                                : 'none',
                                                            transition: 'transform .2s',
                                                        }}
                                                    >
                                                        <ExpandMoreIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </Box>
                                    ) : null}

                                    {app.disabled ? (
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            sx={{ mt: 0.5, fontStyle: 'italic' }}
                                        >
                                            {translate('pos.odoo.soon', { _: 'Coming soon' })}
                                        </Typography>
                                    ) : null}
                                </Box>
                            );
                        })}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
